import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useVideo } from '../contexts/VideoContext';
import { Box, Typography, Button, useTheme, useMediaQuery, Skeleton } from '@mui/material';

const HeroSection: React.FC = () => {
  const { t, isLoading } = useLanguage();
  const { videoEnded, logoAnimationComplete, hideAnimatedLogo, navbarLogoVisible, setVideoEnded, setLogoAnimationComplete, setHideAnimatedLogo, setNavbarLogoVisible } = useVideo();
  const [logoVisible, setLogoVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [showSecondVideo, setShowSecondVideo] = useState(false);
  const [secondVideoVisible, setSecondVideoVisible] = useState(false);
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1485);
  const [videoPlayed, setVideoPlayed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const videoRef = useRef<HTMLVideoElement>(null);
  const secondVideoRef = useRef<HTMLVideoElement>(null);

  const heroContent = {
    title: t('home.hero.title'),
    subtitle: t('home.hero.subtitle'),
    backgroundImage: './images/hero-img3.jpg',
    buttonText: t('home.hero.buttonText'),
  };

  // Detect iOS and track screen width changes
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleVideoEnd = () => {
    setVideoEnded(true);
    setVideoPlayed(true);
    // Start logo animation after a short delay
    console.log(window.innerWidth);
    setTimeout(() => {
      setLogoAnimationComplete(true);
    }, 2000); // 2 seconds for the logo to slide up
    
    // Show navbar logo 4 seconds after video ends
    setTimeout(() => {
      setNavbarLogoVisible(true);
    }, 2800); // 4 seconds after video ends
    
    // Show second video after all animations are complete
    setTimeout(() => {
      setShowSecondVideo(true);
      // Add opacity animation after a short delay
      setTimeout(() => {
        setSecondVideoVisible(true);
      }, 100); // Small delay to ensure video is loaded
    }, 0); // 5 seconds after video ends (after navbar logo appears)
  };

  // Handle video play on user interaction (required for iOS)
  const handleVideoPlay = () => {
    if (videoRef.current && !videoPlayed) {
      videoRef.current.play().catch(console.error);
      setVideoPlayed(true);
    }
  };

  // Handle second video play
  const handleSecondVideoPlay = () => {
    if (secondVideoRef.current) {
      secondVideoRef.current.play().catch(console.error);
    }
  };

  // Calculate responsive logo size based on screen width
  const getLogoSize = () => {
    const baseWidth = 1485; // Reference screen width
    const baseSize = 675; // Base logo size for reference width
    const scaleFactor = screenWidth / baseWidth;
    return Math.max(300, Math.min(800, baseSize * scaleFactor)); // Min 300px, Max 800px
  };

  // Calculate responsive navbar position for logo animation
  const getNavbarPosition = () => {
    const navbarTop = 60; // mt: 2 = 16px
    const navbarHeight = 64; // Approximate navbar height
    const logoSize = 90; // Navbar logo size (fixed)
    const logoTop = navbarTop + (navbarHeight - logoSize) / 2;
    return logoTop;
  };

  // Calculate responsive scale for logo animation
  const getLogoScale = () => {
    const logoSize = getLogoSize();
    const navbarLogoSize = 90; // Target navbar logo size
    return navbarLogoSize / logoSize;
  };

  // Calculate responsive starting position
  const getStartingPosition = () => {
    // Keep logo centered vertically, but adjust for different screen sizes
    return '55%';
  };

  // Handle logo animation completion
  const handleLogoTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    // When the animated logo finishes moving to the navbar position
    if (e.propertyName === 'top' && logoAnimationComplete && logoVisible) {
      // Small delay to let navbar logo fade in, then hide animated logo
      setTimeout(() => {
        setLogoVisible(false);
      }, 300);
    }
  };

  // Calculate video duration and show logo/content at different times
  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const duration = video.duration;
      const currentTime = video.currentTime;
      
      // Show logo 3 seconds before video ends
      if (duration - currentTime <= 3 && !videoEnded) {
        setVideoEnded(true);
        setLogoVisible(true);
        // Start logo animation after fade-in
        setTimeout(() => {
          setLogoAnimationComplete(true);
        }, 2000); // 2 seconds for the logo to slide up
      }
      
      // Show content 1 second before video ends (different timing)
      if (duration - currentTime <= 0.5 && !contentVisible) {
        setContentVisible(true);
      }
      
      // Navbar logo timing is now handled in handleVideoEnd()
    }
  };

  return (
    <Box 
      id="hero-section"
      sx={{
        position: 'relative', 
        width: '100%', 
        height: '100vh', 
        minHeight: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden', 
        margin: 0,
        padding: 0,
        display: 'block',
        backgroundColor: 'rgba(36, 68, 115, 0.75)',
      }}
    >
      {/* First Video Background */}
      <Box
        component="video"
        ref={videoRef}
        autoPlay={!isIOS}
        muted
        playsInline
        preload='metadata'
        webkit-playsinline="true"
        onEnded={handleVideoEnd}
        onTimeUpdate={handleVideoTimeUpdate}
        onTouchStart={isIOS ? handleVideoPlay : undefined}
        onClick={isIOS ? handleVideoPlay : undefined}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          minHeight: '100vh',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 0,
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          perspective: '1000px',
          opacity: showSecondVideo ? 0 : 1,
          transition: 'opacity 2s ease-in-out',
          cursor: isIOS ? 'pointer' : 'default',
          margin: 0,
          padding: 0,
          display: 'block'
        }}
      >
        <source src="/vids/hero-video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </Box>

      {/* Second Video Background */}
      {showSecondVideo && (
        <Box
          component="video"
          ref={secondVideoRef}
          autoPlay={!isIOS}
          muted
          playsInline
          webkit-playsinline="true"
          loop
          onTouchStart={isIOS ? handleSecondVideoPlay : undefined}
          onClick={isIOS ? handleSecondVideoPlay : undefined}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            minHeight: '100vh',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 0,
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            perspective: '1000px',
            opacity: secondVideoVisible ? 1 : 0,
            transition: 'opacity 2s ease-in-out',
            cursor: isIOS ? 'pointer' : 'default',
            margin: 0,
            padding: 0,
            display: 'block'
          }}
        >
          <source src="/vids/hero-2.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </Box>
      )}

      {/* Video Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(36, 68, 115, 0.75)',
          zIndex: 1,
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out'
        }}
      />

      {/* iOS Play Button Overlay */}
      {isIOS && !videoPlayed && (
        <Box
          onClick={handleVideoPlay}
          onTouchStart={handleVideoPlay}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '50%',
            width: 80,
            height: 80,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              transform: 'translate(-50%, -50%) scale(1.1)'
            }
          }}
        >
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: '20px solid white',
              borderTop: '12px solid transparent',
              borderBottom: '12px solid transparent',
              marginLeft: '4px'
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'white',
              fontSize: '0.7rem',
              marginTop: 1,
              textAlign: 'center'
            }}
          >
            Tap to play
          </Typography>
        </Box>
      )}

      {/* Content Overlay */}
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          zIndex: 20, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, md: 6 },
          opacity: contentVisible ? 1 : 0,
          transform: contentVisible ? 'translateX(0)' : 'translateX(50px)',
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: contentVisible ? 'auto' : 'none'
        }}
        className='w-full'
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            // justifyContent: 'center',
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            
            py: 4,
          }}
        >
          {isLoading ? (
            <Box sx={{ mb: 2, width: '100%' }}>
              <Skeleton 
                variant="text" 
                width="90%" 
                height={isMobile ? 60 : 120}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  mb: 1
                }} 
              />
              <Skeleton 
                variant="text" 
                width="70%" 
                height={isMobile ? 60 : 120}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  mb: 1
                }} 
              />
              <Skeleton 
                variant="text" 
                width="60%" 
                height={isMobile ? 60 : 120}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }} 
              />
            </Box>
          ) : (
            <Typography 
              variant={isMobile ? 'h3' : 'h1'} 
              component="h1" 
              className="hero-title"
              sx={{ 
                color: 'white', 
                mb: 2,
                fontSize: { xs: '2rem', md: '4.5rem' },
                lineHeight: 1.2
              }}
            >
              {heroContent.title.split('\n').map((word, index) => (
                <span key={index} className='block '>
                  {word} 
                </span>
              ))}
            </Typography>
          )}

          {isLoading ? (
            <Box sx={{ mb: 3, width: '100%' }}>
              <Skeleton 
                variant="text" 
                width="100%" 
                height={isMobile ? 20 : 30}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  mb: 1
                }} 
              />
              <Skeleton 
                variant="text" 
                width="85%" 
                height={isMobile ? 20 : 30}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }} 
              />
            </Box>
          ) : (
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h2"
              gutterBottom
              className="hero-subtitle"
              sx={{
                color: 'white',
                mb: 3,
                fontSize: { xs: '0.75rem', md: '1.25rem' },
                lineHeight: 1.3
              }}
            >
              {heroContent.subtitle}
            </Typography>
          )}

          {isLoading ? (
            <Skeleton 
              variant="rectangular" 
              width={150} 
              height={48}
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2
              }} 
            />
          ) : (
            <Button
              variant="outlined"
              size="large"
              className="hero-button"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                boxShadow: 3,
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}
              href="/contact"
            >
              {heroContent.buttonText}
            </Button>
          )}
        </Box>
      </Box>


      {/* Animated Logo - Center to Navbar */}
      <Box
        sx={{
          position: 'fixed',
          top: logoAnimationComplete ? `${getNavbarPosition()}px` : getStartingPosition(),
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2000,
          opacity: logoVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out, all 2s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none',
          visibility: logoVisible ? 'visible' : 'hidden'
        }}
        onTransitionEnd={handleLogoTransitionEnd}
      >
          <img 
            src="/images/logo-blue.png" 
            alt="Moktamel" 
            width={getLogoSize()} 
            height={getLogoSize()}
            style={{
              filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.5))',
              transition: 'all 2s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: logoAnimationComplete ? `scale(${getLogoScale()})` : 'scale(1)'
            }}
          />
        </Box>
    </Box>
  );
};

export default HeroSection;